import { createContext, useState, useContext, ReactNode } from 'react';
import { translations } from "@/translations";
import { type Language } from "@/types";

type LanguageContextType = {
  language: Language;
  t: (sectionOrKey: string, key?: string) => string;
  changeLanguage: (lang: Language) => void;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

type LanguageProviderProps = {
  children: ReactNode;
};

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    // Validate saved language or default to en
    if (saved && ['en', 'tr', 'pl', 'de'].includes(saved)) {
      return saved as Language;
    }
    return 'en';
  });

  const changeLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const t = (sectionOrKey: string, key?: string): string => {
    try {
      if (!translations) {
        console.error('Translations object is missing');
        return key || sectionOrKey;
      }

      // 1. Handle nested lookup: t('nav', 'home') 
      // Format: translations[section][language][key]
      if (key) {
        const section = translations[sectionOrKey];
        if (section && section[language]) {
          // Handle dot notation in key if any (e.g. 'categories.all')
          if (key.includes('.')) {
            const parts = key.split('.');
            let current = section[language];
            for (const part of parts) {
              if (current && typeof current === 'object') {
                current = current[part];
              } else {
                current = undefined;
                break;
              }
            }
            if (typeof current === 'string') return current;
          }

          const value = section[language][key];
          if (typeof value === 'string') return value;

          // Fallback to English
          const enValue = section['en'] ? section['en'][key] : undefined;
          if (typeof enValue === 'string') return enValue;
        }
        return key;
      }

      // 2. Handle flat lookup (for phonetic project): t('heroTitle')
      // We expect these in a special 'phonetic' section
      const phoneticSection = translations['phonetic'];
      if (phoneticSection) {
        const value = phoneticSection[language] ? phoneticSection[language][sectionOrKey] : undefined;
        if (typeof value === 'string') return value;

        const enValue = phoneticSection['en'] ? phoneticSection['en'][sectionOrKey] : undefined;
        if (typeof enValue === 'string') return enValue;
      }

      // 3. Fallback to other sections for flat keys
      for (const section of Object.values(translations)) {
        if (section[language] && typeof section[language][sectionOrKey] === 'string') {
          return section[language][sectionOrKey];
        }
      }

      // 4. Last fallback
      return sectionOrKey;
    } catch (error) {
      console.error('Translation error:', error);
      return key || sectionOrKey;
    }
  };

  return (
    <LanguageContext.Provider value={{ language, t, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
